/**
 * Barber card component
 */
import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Barber } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ProtectedAction } from '@/components/shared/ProtectedAction';
import { canManageBarber } from '@/lib/permissions';
import { Users } from 'lucide-react';

interface BarberCardProps {
  barber: Barber;
  onEdit?: (barberId: string) => void;
  onDelete?: (barberId: string) => void;
  onToggleAvailability?: (barberId: string, isAvailable: boolean) => void;
}

export function BarberCard({ barber, onEdit, onDelete, onToggleAvailability }: BarberCardProps) {
  const [queueCount, setQueueCount] = useState<number>(0);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [hasInService, setHasInService] = useState(false);

  // Real-time subscription to barber's queue
  useEffect(() => {
    if (!barber.barberId) {
      setLoadingQueue(false);
      return;
    }

    setLoadingQueue(true);

    // Subscribe to active tickets for this barber - REAL-TIME
    // Include in_service to detect "busy" state, but exclude it from the queue count
    const q = query(
      collection(db, 'queues'),
      where('barberId', '==', barber.barberId),
      where('status', 'in', ['waiting', 'notified', 'arrived', 'in_service'])
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const waitingCount = snapshot.docs.filter(
          doc => doc.data().status !== 'in_service'
        ).length;
        setQueueCount(waitingCount);
        // Check if barber has someone in service (busy)
        const inService = snapshot.docs.some(doc => doc.data().status === 'in_service');
        setHasInService(inService);
        setLoadingQueue(false);
      },
      (error) => {
        console.error('Error subscribing to queue info:', error);
        setLoadingQueue(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [barber.barberId]);

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start gap-4 mb-4">
        {barber.photoURL ? (
          <img
            src={barber.photoURL}
            alt={barber.displayName}
            className="w-16 h-16 rounded-full object-cover"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center text-2xl">
            💈
          </div>
        )}

        <div className="flex-1">
          <h3 className="text-lg font-semibold text-ink">{barber.displayName}</h3>
          {barber.email && (
            <p className="text-sm text-ink-muted">{barber.email}</p>
          )}
          <p className="text-xs text-ink-subtle font-mono">ID: {barber.barberId}</p>

          {/* Status indicators */}
          <div className="flex flex-col gap-1 mt-2">
            {/* Availability */}
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${barber.isAvailable ? 'bg-success/100' : 'bg-surface-3'}`} />
              <span className="text-sm text-ink-muted">
                {barber.isAvailable ? 'Disponible' : 'No disponible'}
              </span>
            </div>

            {/* Busy indicator */}
            {hasInService && (
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-danger/100 animate-pulse" />
                <span className="text-sm text-danger font-medium">
                  Ocupado atendiendo
                </span>
              </div>
            )}

            {/* Queue count */}
            <div className="flex items-center gap-2 mt-1">
              <Users className="w-4 h-4 text-info" />
              <span className="text-sm font-medium text-info">
                {loadingQueue ? '...' : `${queueCount} ${queueCount === 1 ? 'turno' : 'turnos'} en cola`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {barber.bio && (
        <p className="text-sm text-ink-muted mb-3 line-clamp-2">{barber.bio}</p>
      )}

      {barber.specialties.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-ink-subtle mb-2">Specialties:</p>
          <div className="flex flex-wrap gap-2">
            {barber.specialties.map((specialty, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-info/10 text-info text-xs rounded-full"
              >
                {specialty}
              </span>
            ))}
          </div>
        </div>
      )}

      <ProtectedAction requiredPermission={(claims) => canManageBarber(claims, barber.franchiseId)}>
        <div className="space-y-2 pt-4 border-t border-hairline">
          {onToggleAvailability && (
            <Button
              size="sm"
              variant={barber.isAvailable ? 'secondary' : 'primary'}
              onClick={() => onToggleAvailability(barber.barberId, !barber.isAvailable)}
              className="w-full"
            >
              {barber.isAvailable ? 'Set Unavailable' : 'Set Available'}
            </Button>
          )}
          <div className="flex gap-2">
            {onEdit && (
              <Button size="sm" variant="ghost" onClick={() => onEdit(barber.barberId)} className="flex-1">
                Edit
              </Button>
            )}
            {onDelete && (
              <Button size="sm" variant="danger" onClick={() => onDelete(barber.barberId)} className="flex-1">
                Delete
              </Button>
            )}
          </div>
        </div>
      </ProtectedAction>
    </Card>
  );
}
